import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  MenuItem,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { createStaffMember } from "../../api/staffSchedulingApi";
import { listStaffRoles } from "../../api/rolesApi";

const GENDER_OPTIONS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
  { value: "OTHER", label: "Other" },
];

const emptyForm = {
  adult: {
    name: "",
    surname: "",
    email: "",
    phone: "",
    nationality: "",
    allergies: "",
    notes: "",
  },
  staff: {
    username: "",
    policeCheckCompleted: false,
    dslTrained: false,
    firstAidQualified: false,
    roles: [],
  },
  nsr: {
    campus: "",
    arrivalDate: "",
    departureDate: "",
    overnightResident: false,
    gender: "",
  },
};

function normaliseRoleName(role) {
  if (!role) return "";
  if (typeof role === "string") return role;
  return role.name || role.roleName || role.label || "";
}

function cleanUsernamePart(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function buildUsername(name, surname) {
  const first = cleanUsernamePart(name);
  const last = cleanUsernamePart(surname);

  if (!first && !last) return "";
  if (!first) return `${last}@bliss.com`;
  if (!last) return `${first}@bliss.com`;

  return `${first}.${last}@bliss.com`;
}

export default function CreateStaffDialog({
  open,
  onClose,
  campuses = [],
  defaultCampus = "",
  onCreated,
  showToast,
}) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [usernameManuallyEdited, setUsernameManuallyEdited] = useState(false);

  useEffect(() => {
    if (!open) return;

    setUsernameManuallyEdited(false);
    setForm({
      ...emptyForm,
      staff: {
        ...emptyForm.staff,
        username: buildUsername("", ""),
      },
      nsr: {
        ...emptyForm.nsr,
        campus: defaultCampus || "",
      },
    });
  }, [open, defaultCampus]);

  useEffect(() => {
    if (!open) return;

    let active = true;

    const loadRoles = async () => {
      setRolesLoading(true);
      try {
        const result = await listStaffRoles();
        if (!active) return;

        const names = (result || [])
          .map(normaliseRoleName)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));

        setRoleOptions(names);
      } catch (err) {
        if (!active) return;
        setRoleOptions([]);
        showToast?.(err?.message || "Failed to load roles", "error");
      } finally {
        if (active) setRolesLoading(false);
      }
    };

    loadRoles();

    return () => {
      active = false;
    };
  }, [open, showToast]);

  const updateAdult = (field, value) => {
    setForm((prev) => {
      const nextAdult = {
        ...prev.adult,
        [field]: value,
      };

      const next = {
        ...prev,
        adult: nextAdult,
      };

      if (!usernameManuallyEdited && (field === "name" || field === "surname")) {
        next.staff = {
          ...prev.staff,
          username: buildUsername(nextAdult.name, nextAdult.surname),
        };
      }

      return next;
    });
  };

  const updateStaff = (field, value) => {
    setForm((prev) => ({
      ...prev,
      staff: {
        ...prev.staff,
        [field]: value,
      },
    }));
  };

  const updateNsr = (field, value) => {
    setForm((prev) => ({
      ...prev,
      nsr: {
        ...prev.nsr,
        [field]: value,
      },
    }));
  };

  const validationError = useMemo(() => {
    if (!form.adult.name.trim()) return "First name is required";
    if (!form.adult.surname.trim()) return "Surname is required";
    if (!form.staff.username.trim()) return "Username is required";
    if (!form.nsr.campus) return "Campus is required";
    if (!form.nsr.arrivalDate) return "Arrival date is required";
    if (!form.nsr.departureDate) return "Departure date is required";
    if (form.nsr.departureDate < form.nsr.arrivalDate) {
      return "Departure date must be after arrival date";
    }
    if (form.nsr.overnightResident && !form.nsr.gender) {
      return "Gender is required for overnight residents";
    }
    if (!form.staff.roles.length) return "Select at least one staff role";
    return "";
  }, [form]);

  const handleSubmit = async () => {
    if (validationError) {
      showToast?.(validationError, "error");
      return;
    }

    setSaving(true);
    try {
      const result = await createStaffMember(form);
      onCreated?.(result);
    } catch (err) {
      showToast?.(
        err?.message || "Failed to create staff member",
        "error",
        { adultId: err?.body?.adultId || null }
      );
    } finally {
      setSaving(false);
    }
  };

  const loadingMeta = rolesLoading;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create staff</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Adult details
            </Typography>

            {validationError && (
              <Typography variant="body2" color="error">
                {validationError}
              </Typography>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Name"
                value={form.adult.name}
                onChange={(e) => updateAdult("name", e.target.value)}
                fullWidth
              />
              <TextField
                label="Surname"
                value={form.adult.surname}
                onChange={(e) => updateAdult("surname", e.target.value)}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Email"
                value={form.adult.email}
                onChange={(e) => updateAdult("email", e.target.value)}
                fullWidth
              />
              <TextField
                label="Phone"
                value={form.adult.phone}
                onChange={(e) => updateAdult("phone", e.target.value)}
                fullWidth
              />
            </Stack>

            <TextField
              label="Nationality"
              value={form.adult.nationality}
              onChange={(e) => updateAdult("nationality", e.target.value)}
              fullWidth
            />

            <TextField
              label="Allergies"
              value={form.adult.allergies}
              onChange={(e) => updateAdult("allergies", e.target.value)}
              fullWidth
            />

            <TextField
              label="Notes"
              value={form.adult.notes}
              onChange={(e) => updateAdult("notes", e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>

          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Staff details
            </Typography>

            <TextField
              label="Username"
              value={form.staff.username}
              onChange={(e) => {
                setUsernameManuallyEdited(true);
                updateStaff("username", e.target.value);
              }}
              helperText="Auto-generated from name and surname, but can be edited"
              fullWidth
            />

            <Autocomplete
              multiple
              options={roleOptions}
              value={form.staff.roles}
              onChange={(_, value) => updateStaff("roles", value)}
              loading={rolesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Roles"
                  helperText={rolesLoading ? "Loading roles..." : ""}
                />
              )}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.staff.policeCheckCompleted}
                    onChange={(e) =>
                      updateStaff("policeCheckCompleted", e.target.checked)
                    }
                  />
                }
                label="Police Check Completed"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.staff.dslTrained}
                    onChange={(e) => updateStaff("dslTrained", e.target.checked)}
                  />
                }
                label="DSL Trained"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.staff.firstAidQualified}
                    onChange={(e) =>
                      updateStaff("firstAidQualified", e.target.checked)
                    }
                  />
                }
                label="First Aid Qualified"
              />
            </Stack>
          </Stack>

          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              NSR details
            </Typography>

            <TextField
              select
              label="Campus"
              value={form.nsr.campus}
              onChange={(e) => updateNsr("campus", e.target.value)}
              fullWidth
            >
              {campuses.map((campus) => (
                <MenuItem key={campus.id || campus.name} value={campus.name}>
                  {campus.name}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Arrival date"
                type="date"
                value={form.nsr.arrivalDate}
                onChange={(e) => updateNsr("arrivalDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Departure date"
                type="date"
                value={form.nsr.departureDate}
                onChange={(e) => updateNsr("departureDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.nsr.overnightResident}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setForm((prev) => ({
                      ...prev,
                      nsr: {
                        ...prev.nsr,
                        overnightResident: checked,
                        gender: checked ? prev.nsr.gender : "",
                      },
                    }));
                  }}
                />
              }
              label="Overnight resident"
            />

            {form.nsr.overnightResident && (
              <TextField
                select
                label="Gender"
                value={form.nsr.gender}
                onChange={(e) => updateNsr("gender", e.target.value)}
                fullWidth
              >
                {GENDER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>

          {loadingMeta && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading form options...
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || loadingMeta}
        >
          {saving ? "Creating..." : "Create staff"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}